class SoundManager {
  constructor() {
    this.sounds = {};
    this.music = null;
    this.isMuted = false;
    this.musicVolume = 0.5;
    this.soundsVolume = 0.8;
    
    // Charger les préférences depuis le localStorage
    this.loadPreferences();
  }
  
  // Charger les préférences audio
  loadPreferences() {
    try {
      const preferences = JSON.parse(localStorage.getItem('elementalDuelist_settings')) || {};
      
      if (preferences.soundEnabled !== undefined) {
        this.isMuted = !preferences.soundEnabled;
      }
      
      if (preferences.musicEnabled !== undefined) {
        this.musicEnabled = preferences.musicEnabled;
      } else {
        this.musicEnabled = true;
      }
      
      if (preferences.musicVolume !== undefined) {
        this.musicVolume = preferences.musicVolume;
      }
      
      if (preferences.soundsVolume !== undefined) {
        this.soundsVolume = preferences.soundsVolume;
      }
    } catch (error) {
      console.error('Erreur lors du chargement des préférences audio:', error);
    }
  }
  
  // Précharger un son
  preload(id, src) {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      
      audio.addEventListener('canplaythrough', () => {
        this.sounds[id] = audio;
        resolve(audio);
      }, { once: true });
      
      audio.addEventListener('error', (err) => {
        console.error(`Erreur lors du chargement du son ${id}:`, err);
        reject(err);
      });
      
      audio.src = src;
      audio.load();
    });
  }
  
  // Précharger plusieurs sons
  async preloadSounds(soundMap) {
    const promises = [];
    
    for (const [id, src] of Object.entries(soundMap)) {
      promises.push(this.preload(id, src));
    }
    
    try {
      await Promise.all(promises);
      console.log('Tous les sons ont été chargés');
      return true;
    } catch (error) {
      console.error('Erreur lors du chargement des sons:', error);
      return false;
    }
  }
  
  // Jouer un son
  play(id, options = {}) {
    if (this.isMuted) return;
    
    const sound = this.sounds[id];
    if (!sound) {
      console.warn(`Son ${id} non trouvé`);
      return;
    }
    
    // Cloner le son pour permettre de jouer plusieurs instances
    const soundInstance = sound.cloneNode();
    
    // Appliquer les options
    if (options.volume) {
      soundInstance.volume = options.volume * this.soundsVolume;
    } else {
      soundInstance.volume = this.soundsVolume;
    }
    
    if (options.loop) {
      soundInstance.loop = true;
    }
    
    // Jouer le son
    soundInstance.play().catch(error => {
      console.warn(`Erreur lors de la lecture du son ${id}:`, error);
    });
    
    // Nettoyer après la lecture
    soundInstance.addEventListener('ended', () => {
      soundInstance.remove && soundInstance.remove();
    });
    
    return soundInstance;
  }
  
  // Jouer une musique de fond
  playMusic(id, fadeInDuration = 2000) {
    if (this.isMuted || !this.musicEnabled) return;
    
    const music = this.sounds[id];
    if (!music) {
      console.warn(`Musique ${id} non trouvée`);
      return;
    }
    
    // Arrêter la musique précédente
    this.stopMusic(fadeInDuration / 2);
    
    // Configurer la nouvelle musique
    this.music = music.cloneNode();
    this.music.loop = true;
    this.music.volume = 0;
    
    // Jouer et augmenter progressivement le volume
    this.music.play().catch(error => {
      console.warn(`Erreur lors de la lecture de la musique ${id}:`, error);
    });
    
    // Fondu d'entrée
    const startTime = Date.now();
    const fadeAudio = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let newVolume = elapsed / fadeInDuration * this.musicVolume;
      
      if (newVolume >= this.musicVolume) {
        newVolume = this.musicVolume;
        clearInterval(fadeAudio);
      }
      
      if (this.music) {
        this.music.volume = newVolume;
      } else {
        clearInterval(fadeAudio);
      }
    }, 50);
  }
  
  // Arrêter la musique de fond
  stopMusic(fadeOutDuration = 1000) {
    if (!this.music) return;
    
    const music = this.music;
    const startVolume = music.volume;
    const startTime = Date.now();
    
    const fadeAudio = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let newVolume = startVolume - (elapsed / fadeOutDuration * startVolume);
      
      if (newVolume <= 0) {
        newVolume = 0;
        clearInterval(fadeAudio);
        music.pause();
        music.currentTime = 0;
      }
      
      music.volume = newVolume;
    }, 50);
    
    this.music = null;
  }
  
  // Activer/désactiver le son
  toggleMute() {
    this.isMuted = !this.isMuted;
    
    if (this.isMuted) {
      this.stopMusic(500);
    } else if (this.musicEnabled && this.music) {
      this.music.volume = this.musicVolume;
    }
    
    // Sauvegarder la préférence
    this.savePreferences();
    
    return this.isMuted;
  }
  
  // Activer/désactiver la musique
  toggleMusic() {
    this.musicEnabled = !this.musicEnabled;
    
    if (!this.musicEnabled) {
      this.stopMusic(500);
    }
    
    // Sauvegarder la préférence
    this.savePreferences();
    
    return this.musicEnabled;
  }
  
  // Régler le volume de la musique
  setMusicVolume(volume) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    
    if (this.music && !this.isMuted && this.musicEnabled) {
      this.music.volume = this.musicVolume;
    }
    
    // Sauvegarder la préférence
    this.savePreferences();
    
    return this.musicVolume;
  }
  
  // Régler le volume des effets sonores
  setSoundsVolume(volume) {
    this.soundsVolume = Math.max(0, Math.min(1, volume));
    
    // Sauvegarder la préférence
    this.savePreferences();
    
    return this.soundsVolume;
  }
  
  // Sauvegarder les préférences audio
  savePreferences() {
    try {
      const preferences = JSON.parse(localStorage.getItem('elementalDuelist_settings')) || {};
      
      preferences.soundEnabled = !this.isMuted;
      preferences.musicEnabled = this.musicEnabled;
      preferences.musicVolume = this.musicVolume;
      preferences.soundsVolume = this.soundsVolume;
      
      localStorage.setItem('elementalDuelist_settings', JSON.stringify(preferences));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences audio:', error);
    }
  }
}

// Exporter une instance unique
const soundManager = new SoundManager();
export default soundManager;